import {
  AdFieldCurrencyEnum,
  AdFieldMultilistingEnum,
  CompletionStatusEnum,
  CurrencyCommissionEnum,
  TransactionTypeEnum,
} from '../../types/enums';
import type {
  PropertyAdAmenityField,
  PropertyAdEditorFormValues,
  PropertyAdInProgressWorkspace,
} from '../../types/propertyAdEditor';

import { getPropertyAdAmenityValues } from './propertyAdAmenities';

const parseNullableNumericString = (value: string | null | undefined): number | null => {
  if (value === null || value === undefined || value.trim() === '') return null;

  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : null;
};

type NullablePartial<T> = {
  [Field in keyof T]?: T[Field] | null;
};

type PropertyAdEditorHydrationSource = NullablePartial<
  Omit<PropertyAdEditorFormValues, 'addCommission' | 'responsibleUserId' | PropertyAdAmenityField>
> & {
  addCommission?: number | string | null;
  userId?: string | null;
} & Partial<Record<PropertyAdAmenityField, boolean | null>>;

export const mapPropertyAdSourceToEditorForm = (
  source: PropertyAdEditorHydrationSource,
  responsibleDefaultUserId?: string
): PropertyAdEditorFormValues => ({
  responsibleUserId:
    source.userId && source.userId !== responsibleDefaultUserId ? source.userId : null,
  activityKind: source.activityKind ?? null,
  transactionType: source.transactionType ?? null,
  defaultRentFrequency: source.defaultRentFrequency ?? null,
  propertyKind: source.propertyKind ?? null,
  price: source.price ?? null,
  sellingPrice: source.sellingPrice ?? null,
  completionStatus:
    source.completionStatus ??
    (source.transactionType === TransactionTypeEnum.Distress ? CompletionStatusEnum.OffPlan : null),
  toSeller: source.toSeller ?? null,
  toDeveloper: source.toDeveloper ?? null,
  postHandover: source.postHandover ?? null,
  handover: source.handover ? { ...source.handover } : null,
  priceYearly: source.priceYearly ?? null,
  priceMonthly: source.priceMonthly ?? null,
  priceWeekly: source.priceWeekly ?? null,
  priceDaily: source.priceDaily ?? null,
  currency: source.currency ?? AdFieldCurrencyEnum.AED,
  addressEn: source.addressEn ?? null,
  nameEn: source.nameEn ?? null,
  title: source.title ?? '',
  placeId: source.placeId ?? null,
  coordinatesLat: source.coordinatesLat ?? null,
  coordinatesLong: source.coordinatesLong ?? null,
  locationId: source.locationId ?? null,
  districtId: source.districtId ?? null,
  buildingName: source.buildingName ?? null,
  multilisting: source.multilisting ?? AdFieldMultilistingEnum.Covered,
  addCommission:
    typeof source.addCommission === 'number'
      ? source.addCommission
      : parseNullableNumericString(source.addCommission),
  currencyCommission: source.currencyCommission ?? CurrencyCommissionEnum.AED,
  topUp: source.topUp ?? false,
  topUpConditions: source.topUpConditions ?? '',
  description: source.description ?? '',
  builtUpArea: source.builtUpArea ?? null,
  beds: source.beds ?? null,
  baths: source.baths ?? null,
  floor: source.floor ?? null,
  furniture: source.furniture ?? null,
  renovation: source.renovation ?? null,
  occupancy: source.occupancy ?? null,
  rentedTill: source.rentedTill ?? null,
  view: source.view ? [...source.view] : [],
  ...getPropertyAdAmenityValues(source),
});

export const mapWorkspaceToEditorForm = (
  workspace: PropertyAdInProgressWorkspace,
  responsibleDefaultUserId?: string
): PropertyAdEditorFormValues =>
  mapPropertyAdSourceToEditorForm(workspace, responsibleDefaultUserId);
